import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  styled,
  Zoom,
} from '@mui/material';
import { Link as LinkIcon, InfoOutlined } from '@mui/icons-material';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import LeftPanel from '../components/LeftPanel.tsx';
import { useThemeContext } from '../contexts/ThemeContext.tsx';

// --- Stílusok és Animációk ---
const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
`;

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(4px)',
    backgroundColor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.4)',
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: darkMode ? 'white' : '#1a237e',
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
  },
  '& .MuiDialogTitle-root': { 
    fontWeight: 'bold', 
    textAlign: 'center', 
    fontSize: '1.4rem',
    color: darkMode ? 'white' : '#1a237e',
    paddingTop: '24px'
  },
  '& .MuiDialogContentText-root, & .MuiTypography-root': {
    color: darkMode ? 'rgba(255,255,255,0.85)' : '#1a237e',
  }
}));

const DialogActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '10px 32px',
  fontWeight: 'bold',
  color: '#ffffff',
  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.15)' : '#25379c',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.25)' : '#1a237e',
    transform: 'scale(1.05)',
    boxShadow: darkMode ? '0 0 20px rgba(255, 64, 129, 0.5)' : '0 0 20px rgba(33, 203, 243, 0.5)',
  },
}));

const MyContents: React.FC = () => {
  const isAuthenticated = !!Cookies.get('token');
  const navigate = useNavigate();
  const [contents, setContents] = useState<any[]>([]);
  
  // Állapotok a Content Dialoghoz
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);

  // Állapotok az Admin Notes Dialoghoz
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
  const [selectedNoteText, setSelectedNoteText] = useState('');

  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);

  const STATUS_CONFIG = {
    APPROVED: { emoji: '✅', color: '#4caf50' },
    PENDING: { emoji: '⏳', color: '#ff9800' },
    REJECTED: { emoji: '❌', color: '#f44336' }
  };

  useEffect(() => {
    const fetchContent = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/searchmycontent`, {
          headers: {
            'Authorization': `Bearer ${Cookies.get('token')}`
          }
        });
        const data = await response.json();
        setContents(data);
      } catch (error) {
        console.error("Failed to fetch content:", error);
      }
    };

    if (isAuthenticated) {
      fetchContent();
    }
  }, [isAuthenticated]);

  // --- Kezelőfüggvények a Content Preview-hoz ---
  const handleOpenContent = (content: any) => {
    setSelectedContent(content);
    setIsContentDialogOpen(true);
  };
  const handleCloseContent = () => setIsContentDialogOpen(false);
  const handleExitedContent = () => setSelectedContent(null);

  // --- Kezelőfüggvények az Admin Notes-hoz ---
  const handleOpenAdminNotes = (notes: string) => {
    setSelectedNoteText(notes);
    setIsAdminDialogOpen(true);
  };
  const handleCloseAdminNotes = () => setIsAdminDialogOpen(false);
  const handleExitedAdminNotes = () => setSelectedNoteText('');

  const formatUrl = (url: string) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const ContentListSection = ({ items, status }: { items: any[], status: 'APPROVED' | 'PENDING' | 'REJECTED' }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, color: STATUS_CONFIG[status].color }}>
        {STATUS_CONFIG[status].emoji}
        {status}
      </Typography>

      {items.length > 0 ? (
        <List sx={{
          maxHeight: 400,
          overflowY: 'auto',
          border: `2px solid ${STATUS_CONFIG[status].color}20`,
          borderRadius: 2,
          py: 0
        }}>
          {items.map((result, index) => (
            <ListItem
              key={result.id}
              sx={{
                bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.selected' }
              }}
              onClick={() => handleOpenContent(result)}
            >
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">{result.title}</Typography>
                    <Chip
                      label={result.category}
                      size="small"
                      sx={{ bgcolor: STATUS_CONFIG[status].color, color: 'white', fontWeight: 'bold' }}
                    />
                  </Stack>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {result.shortDescription}
                  </Typography>
                }
              />

              <ListItemSecondaryAction>
                <Stack spacing={1} alignItems="flex-end">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      icon={<LinkIcon />}
                      label={result.url?.length > 20 ? `${result.url.slice(0, 20)}...` : result.url}
                      component="a"
                      href={formatUrl(result.url)} 
                      target="_blank"
                      rel="noopener noreferrer" 
                      clickable
                      sx={{ maxWidth: 200 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenAdminNotes(result.adminNotes || '');
                      }}
                      disabled={!result.adminNotes}
                      sx={{ color: result.adminNotes ? STATUS_CONFIG[result.status].color : 'disabled' }}
                    >
                      <InfoOutlined />
                    </IconButton>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    👁️ Views: {result.views.toLocaleString()}
                  </Typography>
                </Stack>
              </ListItemSecondaryAction>

            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 2, fontStyle: 'italic', color: 'text.disabled' }}>
          No {status.toLowerCase()} items
        </Typography>
      )}
    </Box>
  );

  const handlePanelAction = (path: string) => {
    if (path === '/learning') {
      navigate('/learning'); 
    } else if (!isAuthenticated) {
      navigate('/login'); 
    } else {
      navigate(path);
    }
  };

  return (
    <>
      <style>{keyframes}</style>
      <div style={{ position: 'relative', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
        {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

        <LeftPanel
          isAuthenticated={isAuthenticated}
          onLoginRequired={() => navigate('/login')}
          onNavigation={handlePanelAction} 
          open={leftPanelOpen}
          onToggle={() => setLeftPanelOpen(prev => !prev)}
        />

        <Box
          component="main"
          sx={{
            position: 'relative',
            zIndex: 1,
            mt: '64px',
            ml: isSmallScreen ? 0 : (leftPanelOpen ? '240px' : '56px'),
            p: 3,
            transition: 'margin 0.3s ease'
          }}
        >
          <ContentListSection items={contents.filter(i => i.status === 'APPROVED')} status="APPROVED" />
          <ContentListSection items={contents.filter(i => i.status === 'PENDING')} status="PENDING" />
          <ContentListSection items={contents.filter(i => i.status === 'REJECTED')} status="REJECTED" />

          {/* --- ADMIN NOTES DIALOG --- */}
          <AnimatedDialog 
            darkMode={darkMode} 
            open={isAdminDialogOpen} 
            onClose={handleCloseAdminNotes}
            TransitionComponent={Zoom}
            transitionDuration={400}
            TransitionProps={{
              onExited: handleExitedAdminNotes 
            }}
            disableScrollLock
          >
            <DialogTitle>
              🗒️ Admin Notes
            </DialogTitle>
            <DialogContent sx={{ p: 1 , m: 2}}>
              <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
                {isAdminDialogOpen && selectedNoteText === '' 
                  ? 'No notes available' 
                  : selectedNoteText}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
              <DialogActionButton darkMode={darkMode} onClick={handleCloseAdminNotes}>
                Close
              </DialogActionButton>
            </DialogActions>
          </AnimatedDialog>

          {/* --- CONTENT PREVIEW DIALOG --- */}
          <AnimatedDialog
            darkMode={darkMode}
            open={isContentDialogOpen}
            onClose={handleCloseContent}
            maxWidth="md"
            fullWidth
            TransitionComponent={Zoom}
            transitionDuration={400}
            TransitionProps={{
              onExited: handleExitedContent
            }}
            disableScrollLock
          >
            {/* A selectedContent ellenőrzése azért kell, hogy ha lezáródott és onExited lefutott, ne dobjon hibát */}
            {selectedContent && (
              <>
                <DialogTitle sx={{ color: STATUS_CONFIG[selectedContent.status]?.color }}>
                  📄 Content Preview
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{selectedContent.title}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={selectedContent.category}
                        sx={{
                          bgcolor: STATUS_CONFIG[selectedContent.status].color,
                          color: 'white'
                        }}
                      />
                      <Chip
                        icon={<LinkIcon />}
                        label="Visit URL"
                        component="a"
                        href={formatUrl(selectedContent.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                      />
                    </Stack>
                    <Typography variant="body1" paragraph>
                      {selectedContent.shortDescription}
                    </Typography>
                    <Stack direction="row" spacing={3}>
                      <Typography variant="body2">
                        👁️ Views: {selectedContent.views.toLocaleString()}
                      </Typography>
                      <Typography variant="body2">
                        🏷️ Status: {selectedContent.status}
                      </Typography>
                    </Stack>
                    {selectedContent.adminNotes && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          💬 Admin Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedContent.adminNotes}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
                      📅 Created At: {new Date(selectedContent.createdAt).toLocaleString()}
                    </Typography>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
                  <DialogActionButton darkMode={darkMode} onClick={handleCloseContent}>
                    Close
                  </DialogActionButton>
                </DialogActions>
              </>
            )}
          </AnimatedDialog>
        </Box>
      </div>
    </>
  );
};

export default MyContents;